import { useState, useEffect } from "react";
import "./css/PastWorkCard.css";

export default function PastWorkCard({ work, onDelete, onViewDetails, lang = "en" }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculate duration from start_date to now
  const calculateDuration = (startDate) => {
    if (!startDate) return "";
    
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
    const diffMonths = Math.floor((diffTime % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
    
    let duration = "";
    if (diffYears > 0) {
      duration += `${diffYears} ${diffYears === 1 ? "Yr" : "Yrs"}`;
    }
    if (diffMonths > 0) {
      if (duration) duration += " ";
      duration += `${diffMonths} ${diffMonths === 1 ? "Mth" : "Mths"}`;
    }
    
    return duration || "0 Mths";
  };

  // Auto-rotate images if more than one
  useEffect(() => {
    if (!work.photos || work.photos.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % work.photos.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [work.photos]);

  // Get category name - format as "Category - SubCategory" or just title
  const getCategoryName = () => {
    // If sub_categories exist, format as "Category - SubCategory"
    if (work.sub_categories && work.sub_categories.length > 0) {
      const subCat = work.sub_categories[0];
      if (typeof subCat === "object") {
        let categoryName = "";
        let subCategoryName = "";
        
        // Get category name
        if (subCat.category_id && typeof subCat.category_id === "object" && subCat.category_id.name) {
          categoryName = subCat.category_id.name;
        }
        
        // Get sub-category name
        if (subCat.name) {
          subCategoryName = subCat.name;
        }
        
        if (categoryName && subCategoryName) {
          return `${categoryName} - ${subCategoryName}`;
        } else if (categoryName) {
          return categoryName;
        } else if (subCategoryName) {
          return subCategoryName;
        }
      }
    }
    
    // Fallback to title if available
    return work.title || "Work";
  };

  // Format budget
  const formatBudget = (budget) => {
    if (!budget) return "AED 0";
    return `AED ${budget.toLocaleString("en-IN")}`;
  };

  // Handle delete
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    if (onDelete) {
      await onDelete(work._id);
    }
    setIsDeleting(false);
  };

  // Translations
  const t = {
    en: {
      description: "Description",
      delete: "Delete",
      viewDetails: "View Details",
    },
    ar: {
      description: "الوصف",
      delete: "حذف",
      viewDetails: "عرض التفاصيل",
    },
  }[lang];

  const duration = calculateDuration(work.start_date);
  const categoryName = getCategoryName();
  const hasMultipleImages = work.photos && work.photos.length > 1;

  return (
    <div className="past-work-card" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Card Content */}
      <div className="past-work-card-content">
        {/* Image Carousel */}
        <div className="past-work-image-container">
          {work.photos && work.photos.length > 0 ? (
            <div className="past-work-image-wrapper">
              {work.photos.map((photo, index) => (
                <img
                  key={photo.id || index}
                  src={photo.url}
                  alt={work.title || "Work"}
                  className={`past-work-image ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                />
              ))}
              {hasMultipleImages && (
                <div className="image-indicators">
                  {work.photos.map((_, index) => (
                    <span
                      key={index}
                      className={`indicator ${
                        index === currentImageIndex ? "active" : ""
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="past-work-image-placeholder">
              <i className="bx bx-image"></i>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="past-work-content">
          {/* Title */}
          <h3 className="past-work-title">{categoryName}</h3>

          {/* Budget and Duration */}
          <div className="past-work-meta">
            <div className="past-work-meta-item">
              <i className="bx bx-file"></i>
              <span>{formatBudget(work.budget)}</span>
            </div>
            <div className="past-work-meta-item">
              <i className="bx bx-calendar"></i>
              <span>{duration}</span>
            </div>
          </div>

          {/* Description */}
          <div className="past-work-description">
            <strong>{t.description}:</strong>
            <p className="past-work-description-text">{work.description || ""}</p>
          </div>
        </div>
      </div>

      {/* Footer Separator */}
      <div className="past-work-footer-separator"></div>

      {/* Actions */}
      <div className="past-work-actions">
        <button
          className="past-work-delete-btn"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <span className="delete-spinner"></span>
              {t.delete}
            </>
          ) : (
            t.delete
          )}
        </button>
        <button 
          className="past-work-view-btn"
          onClick={() => {
            if (onViewDetails) {
              onViewDetails(work._id);
            }
          }}
        >
          {t.viewDetails} &gt;
        </button>
      </div>
    </div>
  );
}

